import Form from "@/common/components/form/Form";
import { LoginWrapper } from "./style";
import {
  formInputs,
  invitedUserDashboard,
  InvitedUserRole,
  invitedUsers,
  resetPasswordInputs,
  stageToNumber,
} from "./data";
import { LoginProps, RegistrationRouting } from "@/common/types";
import { loginUser } from "@/api";
import useAuth from "@/hooks/useContexts";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ClipSpinner } from "@/common/components/spinner";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import dotsImage from "@/assets/images/dots.png";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { RESET_PASSWORD_URL } from "@/common/constants";
import {
  setPersistent,
  setSession,
  setStoredCompanyData,
} from "@/common/utils/tokenStorage";
import { Modal } from "@/common/components/modal";
import { RedirectModalWrapper } from "@/pages/new-registration/styles";
import { Button } from "@/common/components/button";

export interface ILoginProps {}

export function Login(_: ILoginProps) {
  const [loading, setLoading] = useState(false);
  const { setAuth, goBack, setGoBack } = useAuth();
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [initializeUrl, setInitializeUrl] = useState("");
  const [showRedirectModal, setShowRedirectModal] = useState(false);

  const makeRequest = useApiCallHandling();

  const navigate = useNavigate();
  const location = useLocation() as any;
  const notify = useNotification();

  const from = goBack
    ? location.state?.from?.pathname || "/dashboard/stats"
    : "/dashboard/stats";

  function isInvitedUserRole(role: string): role is InvitedUserRole {
    return (invitedUsers as readonly string[]).includes(role);
  }

  async function onFormSubmit(formValues: LoginProps) {
    setLoading(true);
    try {
      const response = await loginUser(formValues);
      if (response.data.success) {
        const user = response.data.data.user;

        // MED-687: the backend now decides proceed-vs-resume authoritatively
        // instead of this page inferring it from registration_completed/
        // pcn_registration_status below. An incomplete registration carries
        // no token (registration_routing.action === "RESUME_REGISTRATION")
        // and must be handled before anything below touches auth/localStorage.
        const routing: RegistrationRouting | undefined =
          response.data.data.registration_routing;

        if (routing?.action === "RESUME_REGISTRATION") {
          const originBaseUrl = routing.origin_cluster?.frontend_base_url;
          const onAnotherBuild =
            !!originBaseUrl &&
            !originBaseUrl.startsWith(window.location.origin);

          if (onAnotherBuild) {
            notify({
              type: "SUCCESS",
              message:
                "Continue with the rest of your registration on your original portal.",
            });
            // MED-687: land the user directly on their next undone step on
            // the other build, no second login. There's no token to hand
            // off across origins (the backend withholds it, and browser
            // storage doesn't cross origins anyway), so email + the raw
            // resume_step stage name travel in the URL instead — the same
            // pattern the OTP confirmation email link already uses for
            // phone_number/verification_code. The destination build
            // resolves the stage name to its own step number itself; this
            // page only needs to know which build's onboarding entry path
            // to send them to, not its numbering.
            //
            // Reaching this branch already means the origin isn't this
            // build (onAnotherBuild), and there are exactly two builds —
            // so the origin is Kanawa by elimination. A `name === "Kanawa"`
            // check here would be a second, weaker source of truth: the
            // backend's own Cluster#kanawa? is case-insensitive, so a
            // cluster stored "kanawa"/"KANAWA" would pass the backend's
            // check but silently fail this string match and get routed to
            // the wrong build's onboarding entirely.
            const entryPath = "/registration/kyc";
            const params = new URLSearchParams({
              email: user.email,
              resume_step: routing.resume_step || "",
            });
            window.location.href = `${originBaseUrl!.replace(/\/$/, "")}${entryPath}?${params.toString()}`;
            return;
          }

          // No token to store (withheld on purpose - registration isn't
          // complete). companyData is what /register's onboarding steps
          // prefill from, and CompaniesController#create identifies the
          // account from form params rather than a JWT, so this is enough
          // to resume with previously captured data intact.
          //
          // MED-683: this path returns before setPersistent below ever runs, so it
          // sets the store itself. Writing straight to localStorage here would be
          // invisible to /register, which reads via getStoredCompanyData() and would
          // find nothing whenever "Remember me" was last left unchecked. Registration
          // persists, per the product sign-off.
          setPersistent(true);
          setStoredCompanyData(user);

          notify({
            type: "SUCCESS",
            message: "Continue with the rest of your registration",
          });
          navigate(
            `/register?currentStep=${
              stageToNumber[routing.resume_step || ""] || 1
            }`,
            { replace: true }
          );
          return;
        }

        sessionStorage.setItem(
          "regStatus",
          user?.company?.pcn_registration_status
        );

        const token = response.data?.data?.token;
        const authToken = response.data.data.token;
        setAuth({ user, token: authToken });

        // setPersistent must run first: it decides which store setSession writes to.
        setPersistent(Boolean(formValues.persist));
        setSession({ token: authToken, user });
        const role: string = user?.roles?.[0]?.title?.toLowerCase();

        if (user?.roles?.[0]?.designation === "super-admin") {
          navigate("/dashboard/admin/companies/dashboard", { replace: true });
          return;
        } else if (role === "pcn admin") {
          navigate("/dashboard/pcn-admin/invite-companies", { replace: true });
          return;
        } else if (isInvitedUserRole(role)) {
          const path = invitedUserDashboard[role];
          navigate(path, { replace: true });
          return;
        }

        const pcnStage = user?.company?.pcn_registration_status;

        if (!user.email_verified) {
          notify({
            type: "ERROR",
            message: "Please verify your email to continue",
          });

          navigate(
            "/register?currentStep=" +
              stageToNumber["phone_number_verification_stage"] || "1",
            {
              replace: true,
            }
          );

          return;
        }

        if (user.registration_completed && pcnStage === "completed") {
          notify({
            type: "SUCCESS",
            message: "Welcome!, Login successful",
          });
          setGoBack(false);
          navigate(from, { replace: true });
          return;
        }

        if (user.registration_completed && pcnStage === "initialized") {
          notify({
            type: "SUCCESS",
            message:
              "Welcome!, Obtain PCN license to enjoy the full features on Medex",
          });
          setGoBack(false);
          navigate(from, { replace: true });
          return;
        }

        const nextStep = user?.company?.next_step;
        if (nextStep === "phone_number_verification_stage") {
          notify({
            type: "SUCCESS",
            message: "Check your email to continue with email verification",
          });
          return;
        }

        if (!nextStep && pcnStage?.toLowerCase() === "uninitialized") {
          let PCN_BASE_URL = import.meta.env.VITE_PCN_FRONTEND_BASE_URL;
          const url =
            PCN_BASE_URL +
            "self-service/auth/company-registration?token=" +
            token;
          setInitializeUrl(url);
          setShowRedirectModal(true);
          return;
        }

        notify({
          type: "SUCCESS",
          message: "Welcome!, continue with the rest of the registration",
        });
        navigate("/register?currentStep=" + stageToNumber[nextStep] || "1", {
          replace: true,
        });
      }
    } catch (err: any) {
      console.log(err);
      if (err.response?.data?.message) {
        notify({
          type: "ERROR",
          message: err.response.data.message,
        });
      } else {
        notify({
          type: "ERROR",
          message: "Something went wrong, please try again",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(formValues: any) {
    setLoading(true);
    makeRequest(
      RESET_PASSWORD_URL,
      "POST",
      formValues,
      console.log,
      console.log,
      () => setLoading(false)
    );
  }

  const spinnerComponent = (
    <button disabled className="submit">
      <ClipSpinner /> registering
    </button>
  );

  return (
    <LoginWrapper>
      <section className="container">
        <section className="info-sec">
          <div className="text-wrapper">
            <h2>Welcome back !</h2>
            <p>
              Enjoy unlimited access to Drug Supply Management, inventory,
              Real-time Tracking a lot more
            </p>
          </div>
          <img className="dots-image" src={dotsImage} alt="" />
        </section>
        <section className="input-sec">
          <div className="top flex-row">
            <div className="welcome-message">
              <h2 className="sign-up">Log in</h2>
              <p className="other-route">
                Don't have account?{" "}
                <span>
                  <Link className="route-link" to="/register">
                    Create an Account
                  </Link>
                </span>
              </p>
            </div>
          </div>
          <div className="form">
            {showForgotPassword ? (
              <Form
                formInputs={resetPasswordInputs}
                loadingState={loading}
                submitLabel="Send Reset Link"
                processInputs={handleResetPassword}
                spinnerComponent={spinnerComponent}
                login={true}
                biControl={false}
                handleBack={undefined}
                handleForgotPassword={() =>
                  setShowForgotPassword((prev: boolean) => !prev)
                }
                showForgotPassword={showForgotPassword}
                removePadding
              />
            ) : (
              <Form
                formInputs={formInputs}
                loadingState={loading}
                submitLabel="Log In"
                processInputs={onFormSubmit}
                spinnerComponent={spinnerComponent}
                login={true}
                biControl={false}
                handleBack={undefined}
                handleForgotPassword={() =>
                  setShowForgotPassword((prev: boolean) => !prev)
                }
                showForgotPassword={showForgotPassword}
                removePadding
              />
            )}
          </div>
        </section>
      </section>

      <Modal
        title="PCN Self Service Redirect"
        isOpen={showRedirectModal}
        close={() => setShowRedirectModal(false)}
      >
        <RedirectModalWrapper>
          <p>
            You have not started your registration with PCN. You will now be
            redirected to the PCN self service portal for registeration. Click
            on the continue button to continue to PCN self service.
          </p>
          <Button onClick={() => window.open(initializeUrl, "_blank")?.focus()}>
            Continue
          </Button>
        </RedirectModalWrapper>
      </Modal>
    </LoginWrapper>
  );
}
